import { useEventListener } from "@liveblocks/react/suspense"
import { type AlertConfig, Box, useAlert } from "@prismicio/editor-ui"
import { useState } from "react"

import { useCollaborationDataFlag } from "../../../app/integrations/AmplitudeExperiments"
import { useCollaborationState } from "../../collaboration/CollaborationStateProvider"
import { sidePanelPadding, sidePanelWidth } from "../../documentLayout"
import { useDocument } from "../../DocumentProvider"
import { SettingsMenu } from "../SettingsMenu"
import { CreateButton } from "./CreateButton"
import { PublishButton } from "./PublishButton"
import { SaveButton } from "./SaveButton"
import { showUnpublish, UnpublishButton } from "./UnpublishButton"

export type PendingAction =
  | undefined
  | "create"
  | "save"
  | "publish"
  | "release"
  | "unpublish"

export interface PendingActionProps {
  pendingAction: PendingAction
  onPendingActionChange: (action: PendingAction) => void
}

export function HeaderButtons() {
  return (
    <Box
      display="flex"
      gap={8}
      alignItems="flex-start"
      width={sidePanelWidth}
      flexShrink={0}
      padding={{ left: sidePanelPadding("right").left }}
    >
      <Buttons />
      <SettingsMenu />
    </Box>
  )
}

function Buttons() {
  const { data, type } = useDocument()

  const [pendingAction, onPendingActionChange] = useState<PendingAction>()
  const collaborationState = useCollaborationState()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  const buttonProps = {
    pendingAction,
    onPendingActionChange,
  }

  if (type === "existing") {
    const primaryButton = showUnpublish(data.version.status) ? (
      <UnpublishButton key="unpublish" {...buttonProps} />
    ) : (
      <PublishButton key="publish" {...buttonProps} />
    )

    const saveComponent =
      collaborationState.enabled && canUseCollaborationData ? (
        <CollaborationRoomAutoSaveAlert />
      ) : (
        <SaveButton {...buttonProps} />
      )

    return (
      <>
        {saveComponent}
        {primaryButton}
      </>
    )
  }

  return <CreateButton {...buttonProps} />
}

interface actionAlertProps {
  action: NonNullable<PendingAction>
  showAlert: (config: AlertConfig) => void
  onRetry: () => void
}
export function actionAlert(props: actionAlertProps) {
  const { action, showAlert, onRetry } = props

  showAlert({
    title: `Oops! We were unable to ${action} your document.`,
    subtitle: `We encountered an error while trying to ${action} your document. Please contact our support team if this issue persists.`,
    onRetry: onRetry,
  })
}

function CollaborationRoomAutoSaveAlert() {
  const { showAlert } = useAlert()

  useEventListener(({ event }) => {
    if (event.type !== "autosave") return

    if (event.state === "error") {
      showAlert({
        title: "Save failed",
        subtitle:
          "We encountered an error while trying to save your document. Please contact our support team if this issue persists.",
      })
    }
  })

  return null
}
